import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { Decimal } from 'decimal.js';

/**
 * Utilidades para formateo de datos
 * Maneja formateo de dinero, fechas, números y otros tipos de datos
 */

// Habilitar formato relativo (fromNow) y locale español.
dayjs.extend(relativeTime);
dayjs.locale('es');

/**
 * Formatear moneda en pesos dominicanos (RD$)
 */
export const formatCurrencyRD = (amount: number | string | Decimal): string => {
  try {
    const decimal = new Decimal(amount);
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(decimal.toNumber());
  } catch (error) {
    return 'RD$ 0.00';
  }
};

/**
 * Formatear moneda en dólares estadounidenses (US$)
 */
export const formatCurrencyUS = (amount: number | string | Decimal): string => {
  try {
    const decimal = new Decimal(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(decimal.toNumber());
  } catch (error) {
    return 'US$ 0.00';
  }
};

/**
 * Formatear número con separadores de miles
 */
export const formatNumber = (number: number | string | Decimal): string => {
  try {
    const decimal = new Decimal(number);
    return new Intl.NumberFormat('es-DO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(decimal.toNumber());
  } catch (error) {
    return '0';
  }
};

/**
 * Formatear fecha en formato largo
 */
export const formatDate = (date: string | Date): string => {
  try {
    return dayjs(date).format('DD/MM/YYYY');
  } catch (error) {
    return 'Fecha inválida';
  }
};

/**
 * Formatear fecha y hora
 */
export const formatDateTime = (date: string | Date): string => {
  try {
    return dayjs(date).format('DD/MM/YYYY HH:mm');
  } catch (error) {
    return 'Fecha inválida';
  }
};

/**
 * Formatear fecha relativa (hace X tiempo)
 */
export const formatRelativeDate = (date: string | Date): string => {
  try {
    return dayjs(date).fromNow();
  } catch (error) {
    return 'Fecha inválida';
  }
};

/**
 * Formatear nombre completo
 */
export const formatFullName = (firstName: string, lastName: string): string => {
  return `${firstName} ${lastName}`.trim();
};

/**
 * Formatear email para mostrar
 */
export const formatEmail = (email: string): string => {
  return email.toLowerCase().trim();
};

/**
 * Formatear teléfono
 */
export const formatPhone = (phone: string): string => {
  // Remover todos los caracteres no numéricos
  const cleaned = phone.replace(/\D/g, '');

  // Limitar a 10 dígitos para números dominicanos
  const truncated = cleaned.slice(0, 10);

  // Formatear según el patrón dominicano
  if (truncated.length === 10) {
    return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)}-${truncated.slice(6)}`;
  } else if (truncated.length >= 7) {
    return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)}-${truncated.slice(6)}`;
  } else if (truncated.length >= 4) {
    return `(${truncated.slice(0, 3)}) ${truncated.slice(3)}`;
  } else if (truncated.length >= 1) {
    return `(${truncated}`;
  }

  return '';
};

/**
 * Formatear estado del año académico
 */
export const formatYearState = (state: string): string => {
  const states = {
    DRAFT: 'Borrador',
    ACTIVE: 'Activo',
    CLOSED: 'Cerrado',
  };
  return states[state as keyof typeof states] || state;
};

/**
 * Formatear banda de grado
 */
export const formatGradeBand = (band: string): string => {
  const bands = {
    INICIAL: 'Inicial',
    PRIMARIA: 'Primaria',
    SECUNDARIA: 'Secundaria',
  };
  return bands[band as keyof typeof bands] || band;
};

/**
 * Formatear nivel de grado
 */
export const formatGradeLevel = (level: string): string => {
  const levels = {
    // Inicial
    PARVULO_1: 'Párvulo 1',
    PARVULO_2: 'Párvulo 2',
    PRE_KINDER: 'Pre-Kinder',
    KINDER: 'Kinder',
    PRE_PRIMARIO: 'Pre-Primario',

    // Primaria
    PRIMARIA_1: 'Primer Grado',
    PRIMARIA_2: 'Segundo Grado',
    PRIMARIA_3: 'Tercer Grado',
    PRIMARIA_4: 'Cuarto Grado',
    PRIMARIA_5: 'Quinto Grado',
    PRIMARIA_6: 'Sexto Grado',

    // Secundaria
    SECUNDARIA_1: 'Primer Año',
    SECUNDARIA_2: 'Segundo Año',
    SECUNDARIA_3: 'Tercer Año',
    SECUNDARIA_4: 'Cuarto Año',
    SECUNDARIA_5: 'Quinto Año',
    SECUNDARIA_6: 'Sexto Año',
  };
  return levels[level as keyof typeof levels] || level;
};

/**
 * Formatear tipo de tanda
 */
export const formatShiftType = (shift: string): string => {
  const shifts = {
    COMPLETA: 'Tanda Completa',
    MEDIA: 'Tanda Media',
  };
  return shifts[shift as keyof typeof shifts] || shift;
};

/**
 * Formatear rol de usuario
 */
export const formatUserRole = (role: string): string => {
  const roles: Record<string, string> = {
    SUPER_ADMIN: 'Super Admin',
    ADMIN: 'Administrador',
    COORDINATOR: 'Coordinación',
    DIRECTOR: 'Director/a',
    REGISTRY: 'Registro',
    PSYCHOLOGY: 'Psicología',
    SECRETARY: 'Secretaria',
    FINANCE: 'Finanzas',
    INVENTORY: 'Inventario',
    TEACHER: 'Maestro',
    VIEWER: 'Visualizador',
  };
  return roles[role] || role;
};

/**
 * Obtener color para el estado
 */
export const getStateColor = (state: string): string => {
  const colors = {
    DRAFT: 'gray',
    ACTIVE: 'green',
    CLOSED: 'red',
  };
  return colors[state as keyof typeof colors] || 'gray';
};

/**
 * Obtener color para la banda de grado
 */
export const getGradeBandColor = (band: string): string => {
  const colors = {
    INICIAL: 'blue',
    PRIMARIA: 'green',
    SECUNDARIA: 'purple',
  };
  return colors[band as keyof typeof colors] || 'gray';
};

/**
 * Truncar texto largo
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Capitalizar primera letra
 */
export const capitalize = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Formatear bytes a formato legible
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Formatear porcentaje
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Formatear cédula dominicana
 * Formato: 000-0000000-0
 */
export const formatCedula = (cedula: string): string => {
  // Remover todos los caracteres no numéricos
  const cleaned = cedula.replace(/\D/g, '');

  // Limitar a 11 dígitos
  const truncated = cleaned.slice(0, 11);

  if (truncated.length <= 3) {
    return truncated;
  } else if (truncated.length <= 10) {
    return `${truncated.slice(0, 3)}-${truncated.slice(3)}`;
  } else {
    return `${truncated.slice(0, 3)}-${truncated.slice(3, 10)}-${truncated.slice(10)}`;
  }
};
